import { Dispatch, SetStateAction, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import {
  AmphibianForm,
  BatsFeedingAreaForm,
  BatsFlightPathForm,
  BatsForm,
  BirdsBreedingForm,
  CourtshipTerritoryForm,
  FishForm,
  InsectsForm,
  MammalsForm,
  ReptilesForm,
  SamplePointForm,
  PlantForm,
  PhysicalInspectionForm,
  QuickScanForm,
} from '@/components/forms/ObservationLogFormContainer'
import {
  PatchMedia,
  useMutationWrapper,
  usePatchObservationLog,
  useUploadMedia,
} from '@/hooks'
import dayjs, { Dayjs } from 'dayjs'
import {
  ObservationLog,
  PatchObservationLog,
  ModelsWithMedia,
  MediaType,
} from '@/types'
import {
  OTHER_SPECIE_OPTION_VALUE,
  PLACE_FOUND_OPTION_VALUE,
  TRAIL_FOUND_OPTION_VALUE,
  TRAIL_NOT_FOUND_OPTION_VALUE,
  OTHER_GEDRAG_OPTION_VALUE,
  UNKNOWN_BAT_SPECIE_OPTION_VALUE,
  ANIMAL_PRESENT_OPTION_VALUE,
  ANIMAL_NOT_PRESENT_OPTION_VALUE,
} from './fieldsOptions'

export type ObservationLogFormData = Partial<
  Omit<
    PatchObservationLog,
    | 'photos'
    | 'sound_recordings'
    | 'amount'
    | 'amount2'
    | 'amount3'
    | 'amount4'
    | 'amount_per_15_min'
    | 'is_trail_found'
    | 'animal_present'
    | 'first_fledgling_time'
    | 'observed_at'
    | 'research_observation_log_type_id'
  >
> & {
  photos?: PatchMedia
  sound_recordings?: PatchMedia
  sound_recordings2?: PatchMedia
  sound_recordings3?: PatchMedia
  sound_recordings4?: PatchMedia
  amount?: string
  amount2?: string
  amount3?: string
  amount4?: string
  amount_per_15_min?: string
  is_trail_found?: string
  animal_present?: string
  first_fledgling_time?: Dayjs
  observed_at?: Dayjs
}

export type ObservationLogFormContainerProps = {
  data: ObservationLog
  setUpdatedObservationLog: Dispatch<SetStateAction<ObservationLog>>
  onSuccessUpdate: () => void
}

export const ObservationLogFormContainer = ({
  data,
  setUpdatedObservationLog,
  onSuccessUpdate,
}: ObservationLogFormContainerProps): JSX.Element => {
  const { t } = useTranslation()

  const { enqueueSnackbar } = useSnackbar()

  const [isLoading, setIsLoading] = useState(false)

  const {
    mutateAsync: updateObservationLog,
    isLoading: mutateObservationLogInProcess,
    error,
  } = useMutationWrapper(
    usePatchObservationLog.bind(null, {
      id: data.id,
    })
  )

  const { uploadMedia, isLoading: isMediaUploading } = useUploadMedia({
    modelKey: ModelsWithMedia.observationLogs,
  })

  const handleObservationLogSubmit = async (
    formData: ObservationLogFormData
  ) => {
    if (data.research_visit_event && data.research_observation_log_type) {
      setIsLoading(true)

      const observationLogData = {
        ...formData,
        photos: null,
        sound_recordings: null,
        amount: !!formData.amount ? Number(formData.amount) : null,
        amount2: !!formData.amount2 ? Number(formData.amount2) : null,
        amount3: !!formData.amount3 ? Number(formData.amount3) : null,
        amount4: !!formData.amount4 ? Number(formData.amount4) : null,
        research_visit_event_id: data.research_visit_event.id,
        research_observation_log_type_id: data.research_observation_log_type.id,
        is_place_found: formData.residence === PLACE_FOUND_OPTION_VALUE,
        is_trail_found: formData.is_trail_found === TRAIL_FOUND_OPTION_VALUE,
        animal_present: formData.animal_present === ANIMAL_PRESENT_OPTION_VALUE,
        recording_number: formData.recording_number || '',
        recording_number2: formData.recording_number2 || '',
        recording_number3: formData.recording_number3 || '',
        recording_number4: formData.recording_number4 || '',
        amount_per_15_min: Number(formData.amount_per_15_min),
        is_sound_made: !!formData.recording_number,
        observed_at:
          formData.observed_at && dayjs(formData.observed_at).utc().format(),
        first_fledgling_time:
          formData.first_fledgling_time &&
          dayjs(formData.first_fledgling_time).utc().format(),
        related_observation_log_id: !!formData.related_observation_log_id
          ? Number(formData.related_observation_log_id)
          : null,
        other_specie:
          formData?.specie !== OTHER_SPECIE_OPTION_VALUE
            ? ''
            : formData.other_specie,
        specie_spec_other:
          formData?.specie_spec !== OTHER_SPECIE_OPTION_VALUE
            ? ''
            : formData.specie_spec_other,
        unknown_specie:
          formData?.specie !== UNKNOWN_BAT_SPECIE_OPTION_VALUE
            ? ''
            : formData.unknown_specie,
        other_behaviour:
          formData?.behaviour !== OTHER_GEDRAG_OPTION_VALUE
            ? ''
            : formData?.other_behaviour,
      }

      try {
        if (formData.photos?.chosenToUpload || formData.photos?.stale) {
          await uploadMedia({
            media: formData.photos,
            entityId: data.id,
          })
        }

        const mediaUploads = [
          {
            media: formData.sound_recordings,
            extraParams:
              data.research_observation_log_type.code === 'bats_feeding_area'
                ? { forSpecieNumber: 1 }
                : {},
          },
          {
            media: formData.sound_recordings2,
            extraParams: { forSpecieNumber: 2 },
          },
          {
            media: formData.sound_recordings3,
            extraParams: { forSpecieNumber: 3 },
          },
          {
            media: formData.sound_recordings4,
            extraParams: { forSpecieNumber: 4 },
          },
        ]

        const uploadSoundRecords: Promise<void>[] = []

        for (const { media, extraParams } of mediaUploads) {
          if (media?.chosenToUpload || media?.stale) {
            uploadSoundRecords.push(
              uploadMedia({
                media,
                entityId: data.id,
                ...extraParams,
              })
            )
          }
        }

        await Promise.all(uploadSoundRecords)

        await updateObservationLog(observationLogData, {
          successMessageKey:
            'observationLogs:notifications.observationLogChanged',
          onSuccess: (data) => {
            setUpdatedObservationLog(data)
            onSuccessUpdate()
          },
        })
      } catch (e) {
        enqueueSnackbar(`${t('errors:api.execute')}: ${e}`, {
          variant: 'error',
        })
      } finally {
        setIsLoading(false)
      }
    }
  }

  const getSoundRecordDefaultValue = (specieNumber?: number) => ({
    chosenToUpload: [],
    uploadedToApi:
      data?.sound_recordings?.filter((sr) =>
        specieNumber
          ? sr.for_specie_number === specieNumber
          : !sr.for_specie_number
      ) ?? [],
    stale: [],
    mediaType: MediaType.sound_recordings,
    forSpecieNumber: specieNumber,
  })

  const defaultValues = {
    specie: data?.specie ?? '',
    specie2: data?.specie2 ?? '',
    specie3: data?.specie3 ?? '',
    specie4: data?.specie4 ?? '',
    unknown_specie: data?.unknown_specie ?? '',
    other_specie: data?.other_specie ?? '',
    specie_spec: data?.specie_spec ?? '',
    specie_spec_other: data?.specie_spec_other ?? '',
    specie_category: data?.specie_category ?? '',
    include_or_exclude: data?.include_or_exclude ?? '',
    explanation: data?.explanation ?? '',
    box_type: data?.box_type ?? '',
    bat_box_type: data?.bat_box_type ?? '',
    box_number: data?.box_number ?? '',
    amount: data?.amount?.toString() ?? '',
    amount2: data?.amount2?.toString() ?? '',
    amount3: data?.amount3?.toString() ?? '',
    amount4: data?.amount4?.toString() ?? '',
    counting_method: data?.counting_method ?? '',
    counting_method2: data?.counting_method2 ?? '',
    counting_method3: data?.counting_method3 ?? '',
    counting_method4: data?.counting_method4 ?? '',
    behaviour: data?.behaviour ?? '',
    other_behaviour: data?.other_behaviour ?? '',
    behaviour_notes: data?.behaviour_notes ?? '',
    direction: data?.direction ?? '',
    wind_direction: data?.wind_direction ?? '',
    orientation: data?.orientation ?? '',
    height: data?.height ?? '',
    recording_number: data?.recording_number ?? '',
    recording_number2: data?.recording_number2 ?? '',
    recording_number3: data?.recording_number3 ?? '',
    recording_number4: data?.recording_number4 ?? '',
    amount_per_15_min: data?.amount_per_15_min?.toString() ?? '',
    residence: data?.residence ?? '',
    is_trail_found: data?.is_trail_found
      ? TRAIL_FOUND_OPTION_VALUE
      : data?.is_trail_found === false
      ? TRAIL_NOT_FOUND_OPTION_VALUE
      : undefined,
    trail_description: data?.trail_description ?? '',
    animal_present: data?.animal_present
      ? ANIMAL_PRESENT_OPTION_VALUE
      : data?.animal_present === false
      ? ANIMAL_NOT_PRESENT_OPTION_VALUE
      : undefined,
    animal_present_notes: data?.animal_present_notes ?? '',
    place_type: data?.place_type ?? '',
    first_fledgling_time: data?.first_fledgling_time
      ? dayjs(data.first_fledgling_time)
      : undefined,
    place_type_notes: data?.place_type_notes ?? '',
    photos: {
      chosenToUpload: [],
      uploadedToApi: data?.photos ?? [],
      stale: [],
      mediaType: MediaType.photos,
    },
    sound_recordings: getSoundRecordDefaultValue(
      data.research_observation_log_type.code === 'bats_feeding_area'
        ? 1
        : undefined
    ),
    sound_recordings2: getSoundRecordDefaultValue(2),
    sound_recordings3: getSoundRecordDefaultValue(3),
    sound_recordings4: getSoundRecordDefaultValue(4),
    notes: data?.notes ?? '',
    observed_at: data?.observed_at ? dayjs(data.observed_at) : undefined,
    address: data?.address ?? '',
    plant_stage: data?.plant_stage ?? '',
    related_observation_log_id: data.related_observation_log_id,
    research_visit_event_id: data.research_visit_event.id,
  }

  const props = {
    defaultValues,
    onSubmit: handleObservationLogSubmit,
    isLoading: mutateObservationLogInProcess || isMediaUploading || isLoading,
    apiErrorData: error?.data,
  }

  switch (data.research_observation_log_type.code) {
    case 'bats':
      return <BatsForm {...props} />
    case 'bats_feeding_area':
      return <BatsFeedingAreaForm {...props} />
    case 'bats_flight_path':
      return <BatsFlightPathForm {...props} />
    case 'courtship_territory':
      return <CourtshipTerritoryForm {...props} />
    case 'birds_breeding':
      return <BirdsBreedingForm {...props} />
    case 'mammals':
      return <MammalsForm {...props} />
    case 'amphibian_fish':
      return <AmphibianForm {...props} />
    case 'fish':
      return <FishForm {...props} />
    case 'reptiles':
      return <ReptilesForm {...props} />
    case 'insects':
      return <InsectsForm {...props} />
    case 'sample_point':
      return <SamplePointForm {...props} />
    case 'plant':
      return <PlantForm {...props} />
    case 'physical_inspection':
      return <PhysicalInspectionForm {...props} />
    case 'quickscan':
      return <QuickScanForm {...props} />
    default:
      return <></>
  }
}
